// Global axios interceptor for handling authentication errors
import axios from 'axios';
import { fetchAuthSession, signOut } from '@aws-amplify/auth';

let isRefreshing = false;
let failedQueue = [];

// Helper function to detect if a request is to an external API (not the app's API)
const isExternalAPI = (url) => {
  if (!url) return false;
  
  const apiUrl = process.env.REACT_APP_API_URL;
  const externalAPIs = [
    'api.mapbox.com',
    'maps.googleapis.com',
    'api.geocodify.com'
  ];
  
  // Check if URL is to an external API
  const isExternal = externalAPIs.some(domain => url.includes(domain));
  
  // Check if URL is NOT to the app's API
  const isNotAppAPI = apiUrl && !url.includes(apiUrl);
  
  return isExternal || (apiUrl && isNotAppAPI && !url.startsWith('/'));
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Setup axios response interceptor
export const setupAxiosInterceptor = (onLogout) => {
  // Response interceptor
  const interceptorId = axios.interceptors.response.use(
    (response) => {
      // Success response - pass through
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      const requestUrl = originalRequest?.url || error.response?.config?.url || '';

      // Check if this is an external API call
      const isExternal = isExternalAPI(requestUrl);

      // Check for authentication errors (401, 403, or Cognito errors)
      // Exclude 403 errors from external APIs (they're not auth errors)
      const isAuthError = error.response && 
        (error.response.status === 401 || 
         (error.response.status === 403 && !isExternal) ||
         (error.response.status === 400 && 
          (error.response.config?.url?.includes('cognito') || 
           error.response.data?.__type?.includes('NotAuthorizedException') ||
           error.response.data?.__type?.includes('UnauthorizedException'))));

      // Initialize retry count if not present
      if (originalRequest._retryCount === undefined) {
        originalRequest._retryCount = 0;
      }

      // If error is not an auth error, or request was already retried, reject immediately
      // Also check retry count to prevent infinite loops
      if (!error.response || !isAuthError || originalRequest._retry || originalRequest._retryCount >= 1) {
        // For non-auth errors (including external API 403s), reject immediately
        if (!isAuthError) {
          return Promise.reject(error);
        }
        // For auth errors that were already retried or exceeded retry count, proceed to logout
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      // Mark request as retried and increment retry count
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const session = await fetchAuthSession({ forceRefresh: true });
        const newToken = session?.tokens?.idToken?.toString();

        if (newToken) {
          // Token refresh successful - update authorization header
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          // Process queued requests
          processQueue(null, newToken);
          isRefreshing = false;

          // Retry the original request
          return axios(originalRequest);
        } else {
          // No token available - user needs to log in
          throw new Error('No authentication token available');
        }
      } catch (refreshError) {
        // Token refresh failed - user needs to log in
        processQueue(refreshError, null);
        isRefreshing = false;

        // Handle logout
        if (onLogout) {
          await onLogout();
        } else {
          // Default behavior: sign out and redirect to auth
          try {
            await signOut();
            window.location.href = '/auth';
          } catch (signOutError) {
            console.error('Error signing out:', signOutError);
            window.location.href = '/auth';
          }
        }

        return Promise.reject(refreshError);
      }
    }
  );

  return interceptorId;
};

// Cleanup function to remove interceptor if needed
export const removeAxiosInterceptor = (interceptorId) => {
  axios.interceptors.response.eject(interceptorId);
};
